import { sleep } from "../../../library-js/animation/sleep";
import { tween, TweenUpdate, TweenUpdateState } from "../../../library-js/tools/tween";
import { HSLColor } from "../../../library-js/colors/HSLColor";
import { DateHelper } from "../../../library-js/date/DateHelper";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { OnBlur, OnChange, OnClick, OnFocus, OnInput } from "../../../library-js/dom-tools/EventListeners";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { Link } from "../../../library-js/dom-tools/LinkResolver";
import { Request } from "../../../library/ts/html/server/Request";
import { ISOTimeStamp } from "../../../library/ts/vanilla/time/ISOTimeStamp";
import { ClassInfo } from "../../templates/code-doc/ClassInfo";
import { CodeInfo } from "../../templates/code-doc/CodeInfo";
import { MemberInfo } from "../../templates/code-doc/MemberInfo";
import { Rokojori } from "../Rokojori";
import { MathX } from "../../../library-js/math/MathX";
import { HTMLColor } from "../../../library-js/colors/HTMLColor";
import { DataTypesLibrary } from "../../templates/code-doc/DataTypesLibrary";
import { DocSearch } from "../../templates/code-doc/DocSearch";
import { AttributeValue } from "../../../library-js/dom-tools/AttributeValue";
import { MembersInfo } from "../../templates/code-doc/MembersInfo";


export class GodotDocsContent
{
  content:string;
  target:string;
}

export class GodotDocsUpdateData
{
  key:string = "";
  version:string;
  sourcePath:string = "";
  changeDate:ISOTimeStamp;
  content:GodotDocsContent[] = [];
}

export class GodotDocsUpdater
{
  static readonly url = "_php/docs/update-docs.php";

  static readonly editableTarget = new ElementAttribute( "editable-target" );

  static buttonColor = new HSLColor( 0, 0, 80 );
  static buttonColorUnsaved = new HSLColor( 0, 80, 60 );
  static buttonColorCorrect = new HSLColor( 120, 100, 25 );

  static update( e:Element ) 
  {
    let targets = GodotDocsUpdater.editableTarget.queryAll( e );

    let update = new GodotDocsUpdateData();
    update.key = Rokojori.$.cookieSettings.editing;
    update.sourcePath = ClassInfo.sourcePath.fromQueried( e );
    update.changeDate = ISOTimeStamp.now();
    update.version = /godot\/docs\/(\d{1,2}\.\d{1,2})/.exec( window.location + "" )[ 1 ];
    
    targets.forEach(
      ( t )=>
      {
        let value = new GodotDocsContent();
        value.content = t.innerHTML;
        value.target = GodotDocsUpdater.editableTarget.from( t );

        update.content.push( value );
      }
    );

    let updateButton = this.updateButtonClass.query( e ) as HTMLButtonElement;
    this.send( update, updateButton );
  }

  static readonly updateButtonClass = new ClassFlag( "update-button" );

  static applyOnElement( e:Element )  
  {
    let updateButton = ElementType.button.create( "Update Docs", e.ownerDocument );
    this.updateButtonClass.set( updateButton ) ;
    updateButton.style.verticalAlign = "text-top";
    updateButton.style.marginTop = "1em";
    updateButton.style.display ="inline-block";

    let checkButton = ElementType.input.create();
    AttributeValue.type_checkbox.set( checkButton );
    checkButton.style.verticalAlign = "bottom";
    checkButton.style.width = "1.5em";
    checkButton.style.height = "1.5em";
    checkButton.checked = true;
    OnClick.add( updateButton, ( ev )=>  { GodotDocsUpdater.update( e );  })

    let checkLabel = ElementType.span.create( "Editable" );
    checkLabel.style.verticalAlign = "bottom";
    checkLabel.style.marginLeft = "1em";
    let icon = ClassInfo.classIcon.query( e );
    HTMLEditor.insertBefore( icon.parentElement, updateButton, icon );

    // HTMLEditor.insertBefore( icon.parentElement, editableButton, icon );
    HTMLEditor.insertBefore( icon.parentElement, checkLabel, icon );
    HTMLEditor.insertBefore( icon.parentElement, checkButton, icon );
    HTMLEditor.insertBefore( icon.parentElement, ElementType.break.create(), icon );


    this.makeCodeEditable( e );

    OnClick.add( checkButton,
      ()=>
      {
        let targets = GodotDocsUpdater.editableTarget.queryAll( e );
        targets.forEach( t => ElementAttribute.contentEditable.toggleRemove( t, checkButton.checked ) );
      }
    );

    this.createToolBar( e );
  } 

  static cachedSelectionRange:Range;

  static saveSelectionRange(): Range | null
  {
    let selection = window.getSelection();

    if ( ! selection || selection.rangeCount === 0 )
    {
      GodotDocsUpdater.cachedSelectionRange = null;
      return;
    }
    GodotDocsUpdater.cachedSelectionRange = selection.getRangeAt(0).cloneRange();
  }

  static restoreSelectionRange() 
  {
    let selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange( GodotDocsUpdater.cachedSelectionRange );
  }

  static async lookUpDataTypeLink( textSelectionContent:string ):Promise<string>
  {
    let dataTypeLink = await GodotDocsUpdater.getDataTypeLink( textSelectionContent );

    if ( dataTypeLink != null )
    {
      return Promise.resolve( dataTypeLink );
    }

    if ( textSelectionContent.endsWith( "s" ) )
    {
      dataTypeLink = await GodotDocsUpdater.getDataTypeLink( textSelectionContent.substring( 0,textSelectionContent.length - 1 ) );

      if ( dataTypeLink != null )
      {
        return Promise.resolve( dataTypeLink );
      }
    }

    if ( textSelectionContent.endsWith( "es" ) )
    {    
      dataTypeLink = await GodotDocsUpdater.getDataTypeLink( textSelectionContent.substring( 0,textSelectionContent.length - 2 ) );

      if ( dataTypeLink != null )
      {
        return Promise.resolve( dataTypeLink );
      }
    }

    if ( textSelectionContent.endsWith( "ies" ) )
    {    
      dataTypeLink = await GodotDocsUpdater.getDataTypeLink( textSelectionContent.substring( 0,textSelectionContent.length - 3 ) + "y" );

      if ( dataTypeLink != null )
      {
        return Promise.resolve( dataTypeLink );
      }
    }

    return Promise.resolve( null );
  }

  static createToolBar( e:Element )
  {
    let toolbar = Rokojori.$.templatesManager.createTemplate( "godot-docs-toolbar" );
    Rokojori.$.pageHandler.resolveRootPaths( toolbar );
    let classInfo = ClassInfo.type.query( e );
    HTMLEditor.insertBefore( e, toolbar, classInfo );
    
    let inputPrompt = new ElementType( "input-prompt" ).query( e );
    ClassFlag.hidden.set( inputPrompt );

    OnClick.add( toolbar.querySelector( ".create-link" ),
      async ( ev )=>
      { 
        console.log( "clicked" );
        GodotDocsUpdater.saveSelectionRange();

        let targets = GodotDocsUpdater.editableTarget.queryAll( e ) as HTMLElement[];
        let currentSelectedTarget = this.getActiveEditable( targets );

        if ( currentSelectedTarget == null )
        {
          return;
        }

        let promptLabel = new ElementType( "input-prompt-label" ).query( inputPrompt );
        promptLabel.innerHTML = "Set Link"
        ClassFlag.hidden.removeFrom( inputPrompt );
        let textSelectionContent = window.getSelection().toString();

        let dataTypeLink = await GodotDocsUpdater.lookUpDataTypeLink( textSelectionContent );

        let input = ElementType.input.query( inputPrompt );

        let isInternal = false;

        if ( dataTypeLink == null )
        {
          input.value = textSelectionContent;
          
        }
        else
        {
          input.value = dataTypeLink + "-class";
          isInternal = true;
        }
        
        console.log( "active", currentSelectedTarget );
        
        OnClick.add( 
          ClassFlag.ok.query( inputPrompt ) as HTMLElement,
          async ()=>
          {
            input.blur();

            this.restoreSelectionRange();
            let link = input.value;
            let anchor = HTMLEditor.wrapSelection( currentSelectedTarget, "a" );
            let internalClassFlag = new ClassFlag( "internal" );
            let referenceClassFlag = new ClassFlag( "reference" );

            if ( isInternal )
            {
              internalClassFlag.set( anchor );
              referenceClassFlag.set( anchor );
            }

            await DocSearch.createDocLink( anchor as HTMLAnchorElement, link );
            

            await sleep( 500 );

            this.update( e );

            ClassFlag.hidden.set( inputPrompt );
          }
        );

        OnClick.add( 
          ClassFlag.cancel.query( inputPrompt ) as HTMLElement,
          ()=>
          {
            ClassFlag.hidden.set( inputPrompt );
          }
        );
        

        
      }
    );
  }

  static async getDataTypeLinkFrom( n:Node )
  {
    await this.getDataTypeLink( n.textContent );
  }

  static async getDataTypeLink( dataType:string )
  {
    dataType = dataType.replace( /\[\]/, "" );        

    if ( /^List</.test( dataType ) )
    {
      dataType = dataType.replace( /^List</, "" ).replace( />$/, "" );
    }

    let currentPage = window.location + "";
    
    let docsPath = "en/labs/godot/docs/4.4/";

    if ( currentPage.indexOf( "godot/docs/4.3" ) != -1 )
    {
      docsPath = "en/labs/godot/docs/4.3/";
    }
    
    let link = await DataTypesLibrary.getDataTypeLink( dataType, docsPath, Rokojori.$.pageHandler );

    return link;
  }

  static getActiveEditable( elements: HTMLElement[] )
  {
    let selection = window.getSelection();

    if ( ! selection || selection.rangeCount === 0 )
    {
      console.log( "no selection" );
      return null;
    }
    
    let anchorNode = selection.anchorNode;

    if ( ! anchorNode ) 
    {
      console.log( "no anchorNode" );
      return null;
    }

    let parent = GodotDocsUpdater.editableTarget.queryParent( anchorNode as Element );

    if ( parent != null && elements.includes( parent ) )
    {
      return parent; 
    }

    let current: Node | null = anchorNode;

    while ( current && current.nodeType === Node.ELEMENT_NODE ) 
    {
      if ( elements.includes( current as HTMLElement ) ) 
      {
        console.log( "found element", current );
        return current as HTMLElement;
      }

      current = current.parentNode;
    }
    

    return null;
  }

  static makeCodeEditable( e:Element )
  {
    GodotDocsUpdater.queryAndMakeEditable( e, ClassInfo.classTitle );
    GodotDocsUpdater.queryAndMakeEditable( e, ClassInfo.classDescription, "<br>" );

    let memberInfos = MemberInfo.type.queryAll( e );
    memberInfos.forEach(
      ( mi )=>
      {
        GodotDocsUpdater.queryAndMakeEditable( mi, MemberInfo.title );
        GodotDocsUpdater.queryAndMakeEditable( mi, MemberInfo.description );
      }
    )
  }

  static cache = new ElementAttribute( "cache" );
  
  static makeEditable( e:Element, defaultText:string = " " )
  {
    if ( e.textContent.trim() === "" )
    {
      e.innerHTML = `<div>${defaultText}</div>`;
    } 

    if ( ElementAttribute.contentEditable.in( e ) )
    {
      return;
    }

    OnFocus.add( e as HTMLElement, 
      ()=>
      {
        GodotDocsUpdater.cache.to( e, e.innerHTML );
      } 
    );

    OnBlur.add( e as HTMLElement, 
      ()=>
      {
        let cached = GodotDocsUpdater.cache.from( e );

        if ( cached == e.innerHTML )
        {
          console.log( "no-change" );
          return;
        }

        this.update( CodeInfo.type.getParent( e ) );
      } 
    );

    OnInput.add( e as HTMLElement, 
      ()=>
      {
        let button = this.updateButtonClass.query( CodeInfo.type.getParent( e ) ) as HTMLElement;
        button.style.backgroundColor = this.buttonColorUnsaved + "";
        
      }
    );

    ElementAttribute.contentEditable.to( e );

    let target = e.nodeName.toLowerCase();

    if ( MemberInfo.title.is( e ) || MemberInfo.description.is( e ) )
    {
      target = this.getTargetName( e );
     
      // let memberInfo = MemberInfo.type.getParent( e );      
      // let originalName = this.getOriginalName( memberInfo );
      // target = originalName + GodotDocsUpdater.delimiter + target;
    }

    GodotDocsUpdater.editableTarget.to( e, target );
    
  }

  static unifyType( type:string )
  {
    return type.replace( /^\s*\:\s*/, "" ).trim();
  }

  static getTargetName( element:Element )
  {
    let isRoot =  MemberInfo.type.is( element );
    let memberInfo = isRoot ? element : MemberInfo.type.getParent( element ); 
    let targetSignature = this.getOriginalName( memberInfo );

    if ( MemberInfo.method.in( memberInfo ) )
    {
      targetSignature +="(";

      let typeElements = MemberInfo.parameterType.queryAll( memberInfo );
      typeElements = typeElements.filter( t => MembersInfo.ntStyleElement.isParent( t ) );

      let types = typeElements.map( t => t.textContent );
      types = types.map( t => this.unifyType( t ) );

      targetSignature += types.join( "," );
      targetSignature += ")";
      
    }

    targetSignature += ":" + this.unifyType( MemberInfo.dataType.query( memberInfo ).textContent );

    let targetName = targetSignature + GodotDocsUpdater.delimiter;
    
    if ( ! isRoot )
    {
      targetName += element.nodeName.toLowerCase();
    }

    console.log( "TARGET NAME:", targetName, element );

    return targetName;
  }

  static getOriginalName( memberInfo:Element )
  {
    let memberName = MemberInfo.memberName.query( memberInfo );      
    let originalName = CodeInfo.originalMemberName.from( memberName );

    return originalName;
  }

  static delimiter =":::";

  static queryAndMakeEditable<T extends Element>( e:Element, type:ElementType<T>, defaultText:string = " " ) 
  {
    let queried = type.query( e );

    this.makeEditable( queried, defaultText );
  }

  static async send( update:GodotDocsUpdateData, button:HTMLButtonElement )
  {
    let link = Link.Root( "" );
    let relativeURL = Rokojori.$.pageHandler.createRelativeLink( link ).path + GodotDocsUpdater.url;
    let result:{error:boolean,message:string} = await Request.post( relativeURL, update );

    if ( result.error )
    { 
      window.alert( result.message );
    }
    else
    {

      tween( 0.4,
        ( ts:TweenUpdate )=>
        {

          let tri = Math.pow( MathX.triangle( ts.phase ), 0.5 );
          let color = HTMLColor.lerp( this.buttonColor, this.buttonColorCorrect, tri ); 

          button.style.backgroundColor = color + "";
        }
      );

    }
    
  }
}